import "@fortawesome/fontawesome-free/css/all.min.css";
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from "../../../Assets/logo2.png";

const BASE_URL = process.env.REACT_APP_API_URL;


const ChecklistOverview = () => {
  const [checklists, setChecklists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [checklistCount, setChecklistCount] = useState(0);
  const [checklistsQty, setChecklistsQty] = useState(0);
  const [checklistLimitReached, setChecklistLimitReached] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedAuditors, setSelectedAuditors] = useState([]);
  const [currentChecklistId, setCurrentChecklistId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState(null);
  const [assignedItems, setAssignedItems] = useState([]);
  const [initialAssignments, setInitialAssignments] = useState({
    auditors: [],
    branches: []
  });
  const [previousAssignments, setPreviousAssignments] = useState({
    auditors: [],
    branches: []
  });
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedChecklistAssignments, setSelectedChecklistAssignments] = useState([]);
  const [selectedChecklistName, setSelectedChecklistName] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [assignmentToRemove, setAssignmentToRemove] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
  const [sendEmailToDepartment, setSendEmailToDepartment] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const BranchCode = userData?.BranchCode;

  const navigate = useNavigate();
  const location = useLocation();

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  // Fetch checklist count and available limit (checklistsQty)
  useEffect(() => {
    const fetchChecklistLimit = async () => {
      try {
        const response = await fetch(`${BASE_URL}/individual-checklist-limit?BranchCode=${BranchCode}`);
        if (!response.ok) throw new Error("Failed to fetch checklist limit");
        const data = await response.json();
        setChecklistCount(data.checklistCount);
        setChecklistsQty(data.checklistsQty);
        setChecklistLimitReached(data.checklistCount >= data.checklistsQty);
      } catch (error) {
        console.error("Error fetching checklist limit:", error);
      }
    };

    fetchChecklistLimit();
  }, [BranchCode]);

  // Fetch data in useEffect
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await fetchAuditors();
        await fetchChecklists();
        await fetchBranches();
      } catch (error) {
        console.error("Error fetching data:", error);
        notify("Failed to load data", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [BranchCode]);

  // Fetch checklists for the user's company
  const fetchChecklists = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-checklists?BranchCode=${BranchCode}`
      );
      if (!response.ok) throw new Error("Failed to fetch checklists");
      const data = await response.json();
      setChecklists(data);
    } catch (error) {
      console.error("Error fetching checklists:", error);
    }
  };

  // Fetch branches for the user's company
  const fetchBranches = async () => {
    try {
      const response = await fetch(`${BASE_URL}/get-branches?BranchCode=${BranchCode}`);
      if (!response.ok) throw new Error("Failed to fetch Branches");
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // Fetch auditors for the user's company
  const fetchAuditors = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-individual-auditors?BranchCode=${BranchCode}`
      );
      const data = await response.json();
      if (Array.isArray(data)) {
        setAuditors(data);
      } else {
        console.error("Invalid auditors response:", data);
        setAuditors([]);
      }
    } catch (error) {
      console.error("Error fetching auditors:", error);
      setAuditors([]);
    }
  };

  const handleEdit = (id) => {
    navigate(`/EditChecklist/${id}`);
  };

  const openDeleteModal = (id) => {
    setChecklistToDelete(id);
    setShowDeleteModal(true);
  };

  // Update the handleDelete function to handle assignment errors
  const handleDelete = async () => {
    try {
      if (!checklistToDelete) return;
      
      setIsDeleting(true);
      
      const response = await fetch(
        `${BASE_URL}/delete-checklist/${checklistToDelete}`,
        {
          method: "DELETE",
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        // Check if the error is due to assignments
        if (response.status === 409 && data.isAssigned) {
          // Show more informative message
          notify(`Cannot delete this checklist. It is currently assigned to ${data.assignmentCount} ${data.assignmentCount === 1 ? 'auditor' : 'auditors'}.`, "warning");
          setShowDeleteModal(false);
          
          // Optionally show the assignments modal to let them remove assignments first
          const checklist = checklists.find(c => c.id === checklistToDelete);
          if (checklist) {
            handleViewAssignments(checklistToDelete, checklist.checklist_name);
          }
          
          return;
        }
        
        throw new Error(data.error || "Failed to delete checklist");
      }
      
      setChecklists((prev) => prev.filter((checklist) => checklist.id !== checklistToDelete));
      notify("Checklist deleted successfully!", "success");
      setShowDeleteModal(false);
      setChecklistToDelete(null);
    } catch (error) {
      console.error("Error deleting checklist:", error);
      notify("Failed to delete checklist. Please try again.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNew = () => {
    // Check if the checklist count has reached the limit
    if (checklistLimitReached) {
      // Show the modal if the limit is reached
      setShowPurchaseModal(true);
    } else {
      // Otherwise, navigate to the checklist creation page
      navigate("/CreateChecklist");
    }
  };

  // Modal handling
  const handleBuyNow = () => {
    // Handle purchasing logic here
    notify("Redirecting to purchase plan...", "info");
  };

const handleAssign = async (checklistId) => {
  setCurrentChecklistId(checklistId);
  try {
    // Clear previous selections
    setSelectedAuditors([]);
    setSelectedBranches([]);
    
    // Fetch existing assignments
    const response = await fetch(`${BASE_URL}/get-checklist-assignments/${checklistId}`);
    const data = await response.json();
    
    if (data.success) {
      setAssignedItems(data.assignments);
      const existingBranches = data.assignments.map(item => item.branch_id);
      const existingAuditors = data.assignments.map(item => item.auditor_id);
      
      // Set current selections and store as previous assignments
      setSelectedBranches(existingBranches);
      setSelectedAuditors(existingAuditors);
      setPreviousAssignments({
        auditors: existingAuditors,
        branches: existingBranches
      });

      // Check if there's a start date for the first assignment (if any)
      if (data.assignments.length > 0 && data.assignments[0].start_date) {
        // Format the date to YYYY-MM-DD for the input field
        const formattedDate = new Date(data.assignments[0].start_date).toISOString().split('T')[0];
        setStartDate(formattedDate);
      } else {
        // Default to today's date if no existing date
        setStartDate(new Date().toISOString().split('T')[0]);
      }
    } else {
      // If no assignments, reset to today's date
      setStartDate(new Date().toISOString().split('T')[0]);
    }
    
    await fetchAuditors();
    setShowModal(true);
  } catch (error) {
    console.error("Error fetching assignments:", error);
    notify("Failed to fetch existing assignments", "error");
  }
};

const handleBranchToggle = (branchId) => {
    setSelectedBranches([branchId]);
    updateStartDateForSelection([branchId], selectedAuditors);
  };

  const handleAuditorSelect = (auditorId) => {
    setSelectedAuditors([auditorId]);
    updateStartDateForSelection(selectedBranches, [auditorId]);
  };

  // New function to update start date based on selected branch and auditor
  const updateStartDateForSelection = (branches, auditors) => {
    // Only proceed if both a branch and auditor are selected
    if (branches.length > 0 && auditors.length > 0) {
      // Check if this combination already exists in assignedItems
      const existingAssignment = assignedItems.find(item => 
        item.branch_id === branches[0] && 
        item.auditor_id === auditors[0]
      );

      if (existingAssignment && existingAssignment.start_date) {
        // Use existing start date if available
        const formattedDate = new Date(existingAssignment.start_date).toISOString().split('T')[0];
        setStartDate(formattedDate);
      } else {
        // Default to today's date if no existing assignment found
        setStartDate(new Date().toISOString().split('T')[0]);
      }
    }
  };

  const handleSubmitAssignment = async () => {
    try {
      if (!currentChecklistId || selectedAuditors.length === 0 || selectedBranches.length === 0) {
        notify("Please select both branch and auditor", "warning");
        return;
      }

      // Check if this combination already exists
      const existingAssignment = assignedItems.find(item => 
        item.branch_id === selectedBranches[0] && 
        item.auditor_id === selectedAuditors[0]
      );

      if (existingAssignment) {
        notify("This branch and auditor combination is already assigned to this checklist", "warning");
        return;
      }

      // Set default checkbox to true when opening email confirmation modal
      setSendEmailToDepartment(true);
      // Show email confirmation modal instead of directly submitting
      setShowEmailConfirmModal(true);

    } catch (error) {
      console.error("Error in assignment validation:", error);
      notify("An error occurred during validation. Please try again.", "error");
    }
  };

  const handleConfirmAssignment = async () => {
    try {
      setIsAssigning(true);

      // Get branch names for selected branch IDs
      const branchNames = branches
        .filter((branch) => selectedBranches.includes(branch.id))
        .map((branch) => branch.branchName);

      const response = await fetch(`${BASE_URL}/assign-checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checklistId: currentChecklistId,
          auditors: selectedAuditors,
          BranchCode: BranchCode,
          branches: selectedBranches,
          branchNames: branchNames,
          status: "Pending",
          startDate: startDate,
          sendEmailToDepartment: sendEmailToDepartment // Include email preference
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to assign checklist.");
      }

      notify("Checklist assigned successfully!", "success");
      // Update previous assignments after successful assignment
      setPreviousAssignments({
        auditors: selectedAuditors,
        branches: selectedBranches
      });
      setShowModal(false);
      setShowEmailConfirmModal(false);
      setSendEmailToDepartment(false); // Reset checkbox

      // Refresh assignments list if needed
      if (selectedChecklistAssignments.length > 0) {
        handleViewAssignments(currentChecklistId, selectedChecklistName);
      }

    } catch (error) {
      console.error("Error assigning checklist:", error);
      notify(error.message || "Failed to assign checklist. Please try again.", "error");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setShowEmailConfirmModal(false);
    setSelectedAuditors([]);
    setSelectedBranches([]);
    setSendEmailToDepartment(false); // Reset to false when closing
    setPreviousAssignments({
      auditors: [],
      branches: []
    });
  };

  const handleViewAssignments = async (checklistId, checklistName) => {
    try {
      const response = await fetch(`${BASE_URL}/get-checklist-assignments/${checklistId}`);
      const data = await response.json();
      
      
      if (data.success) {
        setSelectedChecklistAssignments(data.assignments);
        setSelectedChecklistName(checklistName);
        setShowAssignmentsModal(true);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      notify("Failed to fetch assignments", "error");
    }
  };

  const confirmRemoveAssignment = (assignment) => {
    setAssignmentToRemove(assignment);
    setShowConfirmModal(true);
  };

  const handleRemoveAssignment = async () => {
    try {
      if (!assignmentToRemove?.id) {
        throw new Error('Assignment ID is missing');
      }

      setIsDeleting(true);

      const response = await fetch(`${BASE_URL}/remove-checklist-assignment/${assignmentToRemove.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove assignment');
      }

      // Update the assignments list in state
      setSelectedChecklistAssignments(prev => 
        prev.filter(assignment => assignment.id !== assignmentToRemove.id)
      );

      notify("Assignment removed successfully", "success");
      setShowConfirmModal(false);
      setAssignmentToRemove(null);

    } catch (error) {
      console.error("Error removing assignment:", error);
      notify(error.message || "Failed to remove assignment", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <ToastContainer />
      
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body d-flex justify-content-between align-items-center">
              <h2 className="card-title mb-0">Checklists</h2>
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <small className="text-muted">Used: </small>
                  <span className="badge bg-primary">{checklistCount}/{checklistsQty}</span>
                </div>
                <button 
                  className={`btn ${checklistLimitReached ? 'btn-warning' : 'btn-primary'} d-flex align-items-center`}
                  onClick={handleAddNew}
                >
                  {checklistLimitReached ? (
                    <>
                      <i className="fas fa-crown me-2"></i>
                      <span className="d-none d-sm-inline">Upgrade to Add</span>
                      <span className="d-sm-none">Upgrade</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus me-2"></i>
                      <span className="d-none d-sm-inline">Add New</span>
                      <span className="d-sm-none">Add</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              {/* Enhanced Loading indicator */}
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-bold mb-1">Loading Checklists</p>
                  <p className="text-muted">Please wait while we fetch your checklist data...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  {checklists.length > 0 ? (
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th scope="col" width="5%">#</th>
                          <th scope="col" width="25%">Checklist Name</th>
                          <th scope="col" width="40%">Description</th>
                          <th scope="col" width="30%">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklists.map((checklist, index) => (
                          <tr key={checklist.id}>
                            <td>{index + 1}</td>
                            <td className="fw-medium">{checklist.checklist_name}</td>
                            <td>
                              <p className="text-muted mb-0 text-truncate">
                                {checklist.description}
                              </p>
                            </td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEdit(checklist.id)}
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                  <span className="d-none d-md-inline ms-1">Edit</span>
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => openDeleteModal(checklist.id)}
                                  title="Delete"
                                  disabled={isDeleting}
                                >
                                  <i className="fas fa-trash"></i>
                                  <span className="d-none d-md-inline ms-1">Delete</span>
                                </button>
                                <button
                                  className="btn btn-outline-info btn-sm"
                                  onClick={() => handleAssign(checklist.id)}
                                  title="Assign"
                                  disabled={isAssigning}
                                >
                                  <i className="fas fa-user-plus"></i>
                                  <span className="d-none d-md-inline ms-1">Assign</span>
                                </button>
                                <button
                                  className="btn btn-outline-info btn-sm"
                                  onClick={() => handleViewAssignments(checklist.id, checklist.checklist_name)}
                                  title="View Assignments"
                                >
                                  <i className="fas fa-tasks"></i>
                                  <span className="d-none d-md-inline ms-1">Assignments</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No checklists found</h5>
                      <p className="text-muted mb-0">Click "Add New" to create your first checklist</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <Modal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        centered
        className="modal-lg"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-crown me-2"></i>
            Upgrade to Premium
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="row align-items-center">
            <div className="col-md-6">
              <img 
                src={logo}
                alt="Upgrade" 
                className="img-fluid mb-3 mb-md-0"
              />
            </div>
            <div className="col-md-6">
              <h4>Unlock Premium Features</h4>
              <ul className="list-unstyled mt-3">
                <li className="mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Unlimited checklists
                </li>
                <li className="mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Advanced analytics
                </li>
                <li className="mb-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Priority support
                </li>
              </ul>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowPurchaseModal(false)}>
            Maybe Later
          </Button>
          <Button variant="primary" onClick={handleBuyNow}>
            <i className="fas fa-arrow-right me-2"></i>
            Upgrade Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Modal */}
      <Modal 
        show={showModal} 
        onHide={handleModalClose} 
        centered
        className="modal-lg"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-user-plus me-2"></i>
            Assign Checklist
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="row">
            <div className="col-md-6 mb-4">
              <Form.Group controlId="branchSelect">
                <h5 className="mb-3">Select Branch</h5>
                <Select
                  value={branches
                    .filter(branch => selectedBranches.includes(branch.id))
                    .map(branch => ({
                      value: branch.id,
                      label: branch.branchName
                    }))}
                  onChange={(selected) => {
                    const branchId = selected ? selected.value : null;
                    if (branchId) {
                      handleBranchToggle(branchId);
                    } else {
                      setSelectedBranches([]);
                      setStartDate(new Date().toISOString().split('T')[0]); // Reset to today if no branch selected
                    }
                  }}
                  options={branches.map(branch => ({
                    value: branch.id,
                    label: branch.branchName
                  }))}
                  isSearchable
                  placeholder="Select a branch..."
                  className="basic-select"
                  classNamePrefix="select"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <h5 className="mb-3">Select Auditor</h5>
                <Select
                  value={auditors
                    .filter(auditor => selectedAuditors.includes(auditor.id))
                    .map(auditor => ({
                      value: auditor.id,
                      label: `${auditor.firstName} ${auditor.lastName}`
                    }))}
                  onChange={(selected) => {
                    const auditorId = selected ? selected.value : null;
                    if (auditorId) {
                      handleAuditorSelect(auditorId);
                    } else {
                      setSelectedAuditors([]);
                      setStartDate(new Date().toISOString().split('T')[0]); // Reset to today if no auditor selected
                    }
                  }}
                  options={auditors.map(auditor => ({
                    value: auditor.id,
                    label: `${auditor.firstName} ${auditor.lastName}`
                  }))}
                  isSearchable
                  placeholder="Select an auditor..."
                  className="basic-select"
                  classNamePrefix="select"
                />
              </Form.Group>
            </div>
          </div>
          
          {/* New Start Date Field */}
          <div className="row mt-3">
            <div className="col-md-12">
              <Form.Group controlId="startDate">
                <h5 className="mb-3">Start Date</h5>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} // Set min to today's date
                  className="form-control"
                />
                <Form.Text className="text-muted">
                  Select the date when the audit should start.
                </Form.Text>
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitAssignment}
            disabled={!selectedAuditors.length || !selectedBranches.length || !startDate || isAssigning}
          >
            {isAssigning ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Assigning...</span>
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Assign Checklist
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Confirmation Modal */}
      <Modal
        show={showEmailConfirmModal}
        onHide={() => {
          setShowEmailConfirmModal(false);
          setSendEmailToDepartment(false);
        }}
        centered
      >
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="fas fa-envelope me-2"></i>
            Email Notification Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="mb-4">
            <h6 className="mb-3">Assignment Summary:</h6>
            <div className="bg-light p-3 rounded">
              <div className="row">
                <div className="col-6">
                  <strong>Branch:</strong>
                </div>
                <div className="col-6">
                  {branches.find(b => b.id === selectedBranches[0])?.branchName || 'N/A'}
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-6">
                  <strong>Auditor:</strong>
                </div>
                <div className="col-6">
                  {auditors.find(a => a.id === selectedAuditors[0]) ? 
                    `${auditors.find(a => a.id === selectedAuditors[0]).firstName} ${auditors.find(a => a.id === selectedAuditors[0]).lastName}` : 
                    'N/A'
                  }
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-6">
                  <strong>Start Date:</strong>
                </div>
                <div className="col-6">
                  {new Date(startDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-top pt-3">
            <h6 className="mb-3">Email Notifications:</h6>
            <div className="alert alert-info d-flex align-items-center mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <span>An email notification will be sent to the assigned auditor.</span>
            </div>
            
            <Form.Check
              type="checkbox"
              id="sendDepartmentEmail"
              label={
                <span>
                  <i className="fas fa-users me-2 text-primary"></i>
                  Also send email notification to department users
                </span>
              }
              checked={sendEmailToDepartment}
              onChange={(e) => setSendEmailToDepartment(e.target.checked)}
              className="mb-2"
            />
            <Form.Text className="text-muted">
              {sendEmailToDepartment ? 
                "Both auditor and department users will receive email notifications." : 
                "Only the auditor will receive an email notification."
              }
            </Form.Text>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              setShowEmailConfirmModal(false);
              setSendEmailToDepartment(false);
            }}
          >
            <i className="fas fa-times me-2"></i>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmAssignment}
            disabled={isAssigning}
          >
            {isAssigning ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Assigning...</span>
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2"></i>
                Confirm & Send
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this checklist?</p>
          <div className="alert alert-warning mt-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> If this checklist is assigned to any auditors, the deletion will fail. 
            You must first remove all assignments before deleting.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Deleting...</span>
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assignments Modal */}
      <Modal
        show={showAssignmentsModal}
        onHide={() => setShowAssignmentsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-info text-white">
          <Modal.Title>
            <i className="fas fa-tasks me-2"></i>
            Manage Assignments - {selectedChecklistName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedChecklistAssignments.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Branch</th>
                    <th>Auditor</th>
                    <th>Start Date</th> {/* New column */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedChecklistAssignments.map((assignment) => (
                    <tr key={assignment.id}>
                      <td>{assignment.branchName}</td>
                      <td>{`${assignment.firstName} ${assignment.lastName}`}</td>
                      <td>{assignment.start_date ? new Date(assignment.start_date).toLocaleDateString() : 'N/A'}</td> {/* Display the start date */}
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmRemoveAssignment(assignment)}
                          disabled={isDeleting}
                        > 
                          {isDeleting && assignmentToRemove?.id === assignment.id ? (
                            <>
                              <div className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></div>
                              <span>Removing...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-trash-alt me-1"></i>
                              Remove
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="fas fa-info-circle fa-2x text-muted mb-3"></i>
              <p className="text-muted mb-0">No assignments found for this checklist.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignmentsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Remove Assignment Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setAssignmentToRemove(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Remove Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assignmentToRemove && (
            <div>
              <p>Are you sure you want to remove this assignment?</p>
              <div className="mt-3 p-3 bg-light rounded">
                <strong>Branch:</strong> {assignmentToRemove.branchName}<br />
                <strong>Auditor:</strong> {assignmentToRemove.firstName} {assignmentToRemove.lastName}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowConfirmModal(false);
              setAssignmentToRemove(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleRemoveAssignment}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Removing...</span>
              </>
            ) : (
              "Remove Assignment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        /* Enhanced loading animation */
        .spinner-grow {
          animation-duration: 1.5s;
        }
        
        
        .text-primary.fw-bold {
          font-size: 1.2rem;
        }
        
        /* Fadeout animation for when loading completes */
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .loading-fadeout {
          animation: fadeOut 0.5s ease-out forwards;
        }
        
        /* Button animations */
        .btn {
          transition: all 0.2s;
        }
        
        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        /* Table styles */
        .table th {
          background-color: #f8f9fa !important;
          font-weight: 600;
        }
        
        /* Card animations */
        .card {
          transition: all 0.3s ease;
        }
        
        /* Modal responsive improvements */
        @media (max-width: 576px) {
          .modal-title {
            font-size: 1.1rem;
          }
          
          .form-label {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ChecklistOverview;
